/**
 * The app's icons (Tabler), at our default size and stroke. Pages use these names instead of importing Tabler
 * directly, so the set (or its look) changes in one place. Icons are decorative: put the words on the control.
 */

import {
  type Icon,
  IconAdjustmentsHorizontal,
  IconAlertOctagon,
  IconAlertTriangle,
  IconAlertTriangleFilled,
  IconArchive,
  IconArchiveOff,
  IconArrowBackUp,
  IconArrowDown,
  IconArrowRight,
  IconArrowsLeftRight,
  IconArrowsSort,
  IconArrowUp,
  IconArticle,
  IconBookmark,
  IconBookmarkFilled,
  IconBookmarks,
  IconBookmarksFilled,
  IconBooks,
  IconBuilding,
  IconCalendarClock,
  IconCalendarPlus,
  IconCategory,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconCirclePlus,
  IconClipboardCheck,
  IconClipboardCheckFilled,
  IconClockCheck,
  IconClockPlus,
  IconClockX,
  IconColumns2,
  IconCopy,
  IconCreditCard,
  IconDeviceLaptop,
  IconDevices,
  IconDots,
  IconExternalLink,
  IconEye,
  IconEyeExclamation,
  IconFile,
  IconFileCode,
  IconFileDescription,
  IconFileExcel,
  IconFileImport,
  IconFileMusic,
  IconFilePlus,
  IconFileSearch,
  IconFileShredder,
  IconFileSpreadsheet,
  IconFiles,
  IconFileText,
  IconFileTypePdf,
  IconFileUpload,
  IconFileWord,
  IconFileZip,
  IconFilter,
  IconFilterCog,
  IconFilterOff,
  IconFocus2,
  IconFolder,
  IconFolderPlus,
  IconHistory,
  IconHistoryOff,
  IconInfoCircle,
  IconInfoCircleFilled,
  IconKey,
  IconLayoutGrid,
  IconLayoutList,
  IconLayoutRows,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
  IconLink,
  IconLinkPlus,
  IconListCheck,
  IconListCheckFilled,
  IconListDetails,
  IconLock,
  IconLogout,
  IconMail,
  IconMenu2,
  IconMessage,
  IconMessageCheck,
  IconMessageCircle,
  IconMessages,
  IconMessagesFilled,
  IconMinus,
  IconMoodPlus,
  IconMoon,
  IconMovie,
  IconPaperclip,
  IconPencil,
  IconPhoto,
  IconPin,
  IconPinFilled,
  IconPlaylistAdd,
  IconPlug,
  IconPlus,
  IconPresentation,
  IconQueuePopOut,
  IconReportAnalytics,
  IconReportAnalyticsFilled,
  IconRestore,
  IconRobot,
  IconSearch,
  IconSelector,
  IconSend,
  IconSettings,
  IconSettingsFilled,
  IconShieldLock,
  IconSitemap,
  IconSocial,
  IconStack2,
  IconStack2Filled,
  IconStackCheck,
  IconSun,
  IconTable,
  IconThumbDown,
  IconTrash,
  IconTrashFilled,
  IconUserCircle,
  IconUserPlus,
  IconUserShare,
  IconUsers,
  IconUsersGroup,
  IconWand,
  IconWebhook,
  IconWorld,
  IconX,
  IconZoomIn,
  IconZoomOut,
  IconZoomScan,
} from "@tabler/icons-preact";
import { isApple } from "../lib/platform.ts";
import { StopXFilled } from "./StopXIcon.tsx";
import { TriageIcon } from "./TriageIcon.tsx";

/**
 * An icon of our own (no universal picture: asking for a review), shown on a narrow screen only: on a wide one, its
 * words alone (docs/ICONS.md). An icon of the app's in its bar, still (the app reads it from what's drawn).
 */
const OWN = "sm:hidden";

/** Size (px) and stroke of icons in text-sized controls. */
const ICON = { size: 16, stroke: 1.75 } as const;

/**
 * One of our icons: Tabler's `I` at our size and stroke; `look` adds classes to every use (a mirror). Sets of icons
 * only a lazy chunk uses are made with it in their own module (office/ribbonIcons.ts), so they stay in that chunk.
 */
function make(I: Icon, look = "") {
  return ({ class: cls, size = ICON.size }: { class?: string; size?: number }) => (
    <I size={size} stroke={ICON.stroke} aria-hidden="true" class={`shrink-0 ${look} ${cls ?? ""}`} />
  );
}

/** Opens a menu or picker (the space switcher): up/down on Apple platforms, down elsewhere, like their selects. */
export const ChooserIcon = make(isApple() ? IconSelector : IconChevronDown);
/** Shows more (or fewer) of something: a card's other fields; opens what's under it (a filter pill's menu or panel). */
/** Sending (an invitation). */
export const SendIcon = make(IconSend);
export const ExpandIcon = make(IconChevronDown);
export const CollapseIcon = make(IconChevronUp);
/** A collapsed region's toggle: points right, and down (rotated) when open. */
export const DiscloseIcon = make(IconChevronRight);
/** Steps back and forth through a list (a document's versions). */
export const PreviousIcon = make(IconChevronLeft);
export const NextIcon = make(IconChevronRight);
/** Two of something side by side (a version beside the one before it). */
export const CompareIcon = make(IconColumns2);
/** A version saved as a new document. */
export const NewDocumentIcon = make(IconFilePlus);
/** A version restored: brought back, as restoring from the trash (not the History section's own clock). */
export const RestoreVersionIcon = make(IconRestore);
export const EraseHistoryIcon = make(IconHistoryOff, OWN);
/** Adds entries to a list (options from the values in use). */
export const AddToListIcon = make(IconPlaylistAdd, OWN);
export const UploadIcon = make(IconFileUpload);
/** One of several copies of the same thing (an item in more than one board column). */
export const CopiesIcon = make(IconCopy);
/** Copies something (an id, beside it). */
export const CopyIcon = make(IconCopy);
/** Opens the navigation (small screens). */
export const HamburgerIcon = make(IconMenu2);
/** Removes or dismisses. */
export const CloseIcon = make(IconX);
export const ArrowUpIcon = make(IconArrowUp);
export const ArrowDownIcon = make(IconArrowDown);
/** A column that can be sorted (not sorted by now). */
export const SortableIcon = make(IconArrowsSort);
/** More actions, in a menu (an item's less common ones). */
export const MoreIcon = make(IconDots);
/** More settings for a row (a field's). */
export const SlidersIcon = make(IconAdjustmentsHorizontal);
/** A list's filters (adding one). */
export const FilterIcon = make(IconFilter);
/** A filter by a field's value (a condition on it). */
export const FieldConditionIcon = make(IconFilterCog);
/** A list's filters: what kind of item, when it was added or last changed, who sees it, inside files. */
export const KindFilterIcon = make(IconCategory);
export const AddedIcon = make(IconCalendarPlus);
export const UpdatedIcon = make(IconCalendarClock);
export const SeenByIcon = make(IconEye);
/** Seen by more people than the folder it's in. */
export const SeenWiderIcon = make(IconEyeExclamation);
export const InFilesIcon = make(IconFiles);
export const PaperclipIcon = make(IconPaperclip);
/** A finding closed as fixed, or as wrong. */
export const ResolvedIcon = make(IconCheck);
/** The current choice, in a menu. */
export const CheckIcon = make(IconCheck);
/** Private: only the people on its list see it. */
export const PrivateIcon = make(IconLock);
/** The organization's: everyone in the space sees it. */
export const OrgIcon = make(IconUsersGroup);
/** Public: anyone with its link sees it. */
export const PublicIcon = make(IconWorld);
/** A link to copy (an item's, to send someone). */
export const LinkIcon = make(IconLink);
/** Opens in a new tab (a page elsewhere). */
export const NewTabIcon = make(IconExternalLink);
/** Signing out. */
export const SignOutIcon = make(IconLogout);
export const DismissedIcon = make(IconThumbDown);
/** A conversation (comments on something). */
export const CommentIcon = make(IconMessageCircle);
/** Adds a reaction (an emoji) to a comment. */
export const ReactIcon = make(IconMoodPlus);
/** Who's who: an agent, an integration. */
export const AgentIcon = make(IconRobot);
export const IntegrationIcon = make(IconPlug);
/** Pinned to the sidebar (filled), or not. */
export const PinIcon = make(IconPin);
export const PinnedIcon = make(IconPinFilled);
/** Bookmarked (filled), or not. */
export const BookmarkIcon = make(IconBookmark);
export const BookmarkedIcon = make(IconBookmarkFilled);
/** Opening something in the side panel (a quick look). */
export const PeekIcon = make(IconQueuePopOut);
/** A pane moving to the other side. */
export const SwapSidesIcon = make(IconArrowsLeftRight);
/** A zip file (a space export, an item to unpack). */
export const ZipIcon = make(IconFileZip);
/** Archiving an item (out of lists, counts and queues), and the archived ones. */
export const ArchivedIcon = make(IconArchive);
/** Bringing back: from the trash, a name as it came. */
export const RestoreIcon = make(IconRestore);
/** Taking out of the archived ones. */
export const UnarchiveIcon = make(IconArchiveOff);
/** An import going into the space. */
export const ImportingIcon = make(IconFileImport);
/** A search field's magnifier. */
export const SearchIcon = make(IconSearch);
/** Linking an item (searching for one to link). */
export const AddLinkIcon = make(IconLinkPlus);
/** Normalizing values (one way of writing each, across items). */
export const NormalizeIcon = make(IconWand, OWN);
/** Clearing a list's filters. */
export const ClearFiltersIcon = make(IconFilterOff);
/** A list shown as a table, or as cards (picking how). */
export const TableViewIcon = make(IconTable);
export const CardsViewIcon = make(IconLayoutGrid);
/** An icon drawn here from `paths`, in Tabler's conventions: a 24 grid, the icons' stroke, round caps and joins. */
function drawn(paths: readonly string[], look = "") {
  return ({ class: cls, size = ICON.size }: { class?: string; size?: number }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width={ICON.stroke}
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      class={`shrink-0 ${look} ${cls ?? ""}`}
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
/**
 * How a list is shown (its Display menu, of switches): Tabler's toggle-left over its toggle-right, each drawn smaller
 * to stack (pills 18 by 8, 4 apart, the knobs inset as theirs are).
 */
export const DisplayIcon = drawn(
  [
    "M3 6a4 4 0 0 1 4 -4h10a4 4 0 0 1 0 8h-10a4 4 0 0 1 -4 -4",
    "M5.5 6a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0",
    "M3 18a4 4 0 0 1 4 -4h10a4 4 0 0 1 0 8h-10a4 4 0 0 1 -4 -4",
    "M15.5 18a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0",
  ],
  OWN,
);
/** Switching to the dark theme, or to the light one. */
export const DarkIcon = make(IconMoon);
/** Saving what's there as a version that stays (a moment added to its history)… */
export const SaveVersionIcon = make(IconClockPlus, OWN);
/** …and nothing changed since it was saved. */
export const SavedVersionIcon = make(IconClockCheck);
/** The panel beside a page (its issues and reviews): hide it, show it. */
export const HideSideIcon = make(IconLayoutSidebarRightCollapse);
export const ShowSideIcon = make(IconLayoutSidebarRightExpand);
/** The sidebar (a space's sections): hide it, bring it back. */
export const HideNavIcon = make(IconLayoutSidebarLeftCollapse);
export const ShowNavIcon = make(IconLayoutSidebarLeftExpand);
/** A document in pages, one under another. */
export const PagedIcon = make(IconLayoutList);
/** A document scrolled as one column, not cut into pages. */
export const ContinuousIcon = make(IconLayoutRows);
/** A document read as one column at the screen's width (free flow). */
export const FlowIcon = make(IconArticle);
export const LightIcon = make(IconSun);
/** The entity catalog. */
export const CatalogIcon = make(IconBooks);
/** Choosing from the catalog, like shopping: into the basket, in it (chosen), and the selection itself. */
/**
 * A shopping basket drawn for the catalog (Tabler's doesn't read as one at small sizes), in the same conventions: a 24
 * grid, the icons' stroke, round caps and joins, no fill. A rim, a tapered body and two handles opening upward, a little
 * to the left so a badge fits top right: a plus (add to it) or a check (in it); alone, centred.
 */
function basket(more: string[], shift?: string) {
  return ({ class: cls, size = ICON.size }: { class?: string; size?: number }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width={ICON.stroke}
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      class={`shrink-0 ${cls ?? ""}`}
    >
      <g transform={shift}>
        {BASKET.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      {more.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
const BASKET = [
  "M2.5 11h15",
  "M3.8 11l1.2 6.8a1.8 1.8 0 0 0 1.8 1.4h7.4a1.8 1.8 0 0 0 1.8 -1.4l1.2 -6.8",
  "M5.8 5.5l2 5.5",
  "M14.2 5.5l-2 5.5",
];
export const AddToBasketIcon = basket(["M20 3v5", "M17.5 5.5h5"]);
export const InBasketIcon = basket(["M17.25 5.75l1.75 1.75l3.25 -3.5"]);
/** The basket alone (the selection panel heading): the same drawing, centred since it has no badge. */
export const BasketIcon = basket([], "translate(2 0.5)");
/**
 * A folder, as a folder (the site's drawing of a filed box); the tree's link groups have their own (LinkIcons).
 * @public site/ draws it (outside this package, which knip doesn't see).
 */
export const FolderIcon = make(IconFolder);
export const NewFolderIcon = make(IconFolderPlus);

export const ZoomInIcon = make(IconZoomIn);
export const ZoomOutIcon = make(IconZoomOut);
/** Zoom to fit everything in view. */
export const FitIcon = make(IconZoomScan);
/** Show only something's own links (or its part of a diagram). */
export const FocusIcon = make(IconFocus2);
export const MinusIcon = make(IconMinus);
export const PlusIcon = make(IconPlus);
/** Reviewing an item, and asking someone to (a pair: the review queue's clipboard, checked, or addressed to someone). */
export const ReviewIcon = make(IconClipboardCheck, OWN);
/**
 * Tabler's clipboard with its at in it, where the check is (Tabler has none): the at drawn again smaller, at the same
 * stroke (shrunk, its rings ran together), its tail and its sweep as Tabler's.
 */
export const RequestReviewIcon = drawn(
  [
    "M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2",
    "M9 5a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2",
    "M10.75 14a1.25 1.25 0 1 0 2.5 0a1.25 1.25 0 1 0 -2.5 0",
    "M13.25 14v0.7a1.375 1.375 0 0 0 2.75 0v-0.7a4 4 0 1 0 -2.44 3.68",
  ],
  OWN,
);
/** Marking everything said as read. */
export const MarkReadIcon = make(IconMessageCheck, OWN);
/** The entity map's portals, as many as suit it. */
export const AutoPortalsIcon = make(IconSocial);
/** Removing things. */
export const TrashIcon = make(IconTrash);
/** Deleting for good (from the trash): shredded, past restoring. */
export const ShredIcon = make(IconFileShredder);
/** Deleting for good what's been in the trash a while (a cleanup by age). */
export const CleanupIcon = make(IconClockX, OWN);

/** The sidebar's sections, by path. */
export const NAV_ICONS = {
  triage: TriageIcon,
  queue: make(IconClipboardCheck),
  conversation: make(IconMessages),
  reports: make(IconReportAnalytics),
  items: make(IconStack2),
  rules: make(IconListCheck),
  // Mirrored: Tabler's arrow runs clockwise (forward); history looks back, counterclockwise.
  history: make(IconHistory, "-scale-x-100"),
  bookmarks: make(IconBookmarks),
  trash: make(IconTrash),
  settings: make(IconSettings),
} as const;

/** The sections' icons filled, where Tabler has them: a native app's selected tab. */
export const NAV_FILLED_ICONS: Partial<Record<keyof typeof NAV_ICONS, ReturnType<typeof make>>> = {
  queue: make(IconClipboardCheckFilled),
  conversation: make(IconMessagesFilled),
  reports: make(IconReportAnalyticsFilled),
  items: make(IconStack2Filled),
  rules: make(IconListCheckFilled),
  bookmarks: make(IconBookmarksFilled),
  trash: make(IconTrashFilled),
  settings: make(IconSettingsFilled),
};

/** The Items section's other pages: the entities (wherever they're named as a section), their map, the issues. */
export const EntitiesIcon = make(IconStackCheck);
export const MapIcon = make(IconSitemap);
export const IssuesIcon = make(IconAlertTriangle);

/** Settings pages, by path below `/settings` (the space's and the organization's own: the sidebar's gear). */
export const SETTINGS_ICONS = {
  "/hooks": make(IconWebhook),
  "/spaces": make(IconLayoutGrid),
  "/members": make(IconUsers),
  "/billing": make(IconCreditCard),
  "/usage": make(IconChartBar),
  "/keys": make(IconKey),
  "/privacy": make(IconShieldLock),
} as const;

/** Your account's pages, by path. */
export const ACCOUNT_ICONS = {
  "/profile": make(IconUserCircle),
  "/profile/orgs": make(IconBuilding),
  "/profile/sessions": make(IconDevices),
  "/profile/privacy": make(IconShieldLock),
  "/profile/device": make(IconDeviceLaptop),
} as const;

/** Inviting someone to the organization. */
export const InviteIcon = make(IconUserPlus);

/** A file's original as it came (its static preview), instead of Revisa's viewer. */
export const OriginalIcon = make(IconFileSearch);

/** What an item is, by kind. */
export const KIND_ICONS = {
  email: make(IconMail),
  message: make(IconMessage),
  webpage: make(IconWorld),
  file: make(IconFile),
  record: make(IconListDetails),
} as const;

/** A file by its sort (see `fileType`): the icons, colored by convention in `FileTypeIcon`. */
export const FILE_ICONS = {
  "revisa-doc": make(IconFileDescription),
  "revisa-sheet": make(IconTable),
  pdf: make(IconFileTypePdf),
  word: make(IconFileWord),
  "text-document": make(IconFileText),
  pages: make(IconFileText),
  excel: make(IconFileExcel),
  spreadsheet: make(IconFileSpreadsheet),
  numbers: make(IconFileSpreadsheet),
  presentation: make(IconPresentation),
  keynote: make(IconPresentation),
  image: make(IconPhoto),
  video: make(IconMovie),
  audio: make(IconFileMusic),
  archive: make(IconFileZip),
  code: make(IconFileCode),
  text: make(IconFileText),
} as const;

/** A change that made something (history). */
export const CreatedIcon = make(IconCirclePlus);
/** A change that edited something. */
export const EditedIcon = make(IconPencil);
/** Renaming something (an item's title). */
export const RenameIcon = make(IconPencil);
/** What a name becomes (a rename's preview). */
export const BecomesIcon = make(IconArrowRight);
/** A change that deleted something. */
export const DeletedIcon = make(IconTrash);
/** A change that undid an earlier one. */
export const UndoIcon = make(IconArrowBackUp);
/** Guests added to or taken off an item. */
export const GuestIcon = make(IconUserShare);
/** The org's plan: something to act on (a trial's last days, read-only, a limit reached). */
export const PlanAlertIcon = make(IconAlertTriangle);

/** Issue levels (and the triage area), as counts show them: outlined, or filled. */
export const LEVEL_ICONS = {
  error: [make(IconAlertOctagon), StopXFilled],
  warning: [make(IconAlertTriangle), make(IconAlertTriangleFilled)],
  info: [make(IconInfoCircle), make(IconInfoCircleFilled)],
  triage: [TriageIcon, TriageIcon],
} as const;
